import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import Linescore from './Linescore';
import BattingLines from './BattingLines';
import PitchingLines from './PitchingLines';
import PlayByPlay from './PlayByPlay';

const POLL_INTERVAL = 15000;

const Gamecast = ({ gamePk, leagueId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('box');
  const pollRef = useRef(null);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const response = await leaguesAPI.getGameDetail(leagueId, gamePk);
      if (response.success) setData(response);
    } catch (err) {
      console.error('Error fetching gamecast:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId, gamePk]);

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(() => fetchData(true), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={`p-8 text-center ${dynastyTheme.classes.text.neutralLight}`}>
        Loading gamecast...
      </div>
    );
  }

  if (!data) return null;

  const gi = data.game_info || {};
  const isLive = gi.status_code === 'I';
  const isFinal = gi.status_code === 'F' || gi.status_code === 'O';

  let statusText = gi.status || 'Scheduled';
  if (isLive) {
    const half = gi.inning_state === 'Top' ? 'Top' : 'Bot';
    statusText = `${half} ${gi.inning}`;
  }

  const sections = [
    { id: 'box', label: 'Box Score' },
    { id: 'plays', label: 'Play-by-Play' },
  ];

  return (
    <div className="space-y-3">
      {/* Back button + game header */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-3">
          <button onClick={onBack} className={`flex items-center gap-1 text-xs ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} mb-3`}>
            <ArrowLeft className="w-3.5 h-3.5" /> All Games
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className={`text-sm font-bold ${dynastyTheme.classes.text.white}`}>{gi.away_team}</div>
                <div className={`text-3xl font-black ${dynastyTheme.classes.text.white}`}>{gi.away_score}</div>
              </div>
              <span className={`text-lg ${dynastyTheme.classes.text.neutralLight}`}>@</span>
              <div className="text-center">
                <div className={`text-sm font-bold ${dynastyTheme.classes.text.white}`}>{gi.home_team}</div>
                <div className={`text-3xl font-black ${dynastyTheme.classes.text.white}`}>{gi.home_score}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isLive && <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse"></span>}
              <span className={`text-sm font-bold ${isLive ? dynastyTheme.classes.text.success : isFinal ? dynastyTheme.classes.text.neutralLight : dynastyTheme.classes.text.white}`}>
                {statusText}
              </span>
            </div>
          </div>
        </div>

        {/* Linescore */}
        <Linescore linescore={data.linescore} gameInfo={gi} />
      </div>

      {/* Section tabs */}
      <div className={dynastyTheme.components.card.base}>
        <div className={`flex items-center gap-2 px-4 py-2 border-b ${dynastyTheme.classes.border.neutral}`}>
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all
                ${activeSection === s.id
                  ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                  : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {activeSection === 'box' && (
          <div className="p-4 space-y-4">
            <BattingLines batting={data.batting?.away} />
            <BattingLines batting={data.batting?.home} />
            <PitchingLines pitching={data.pitching?.away} />
            <PitchingLines pitching={data.pitching?.home} />

            {data.game_notes?.length > 0 && (
              <div style={{ padding: '8px 12px', fontSize: 10, color: '#9ca3af', lineHeight: 1.6, borderTop: '1px solid #2d2d2d' }}>
                {data.game_notes.map((note, i) => (
                  <span key={i}>
                    <span style={{ color: '#eab308', fontWeight: 600 }}>{note.label}:</span>{' '}
                    <span>{note.value}</span>
                    {i < data.game_notes.length - 1 && ' · '}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSection === 'plays' && (
          <PlayByPlay plays={data.plays} />
        )}
      </div>
    </div>
  );
};

export default Gamecast;
